import { useEffect, useState } from 'react';
import { useSort } from './useSort';
import { Method, useFetch } from './useFetch';

export interface CurrentProgress {
  completed: boolean;
  currentStep: ProgressStep;
  percentage: number;
  reset(): void;
  start(): void;
  summary: ProgressStepSummary[];
  totalSteps: number;
}

interface ProgressError {
  code: string | null;
  reason: string;
}

export interface ProgressStep {
  step: number;
  action?(): void;
  axiosAction?(
    makeRequest: (url: string, method: Method, body?: any) => void
  ): void;
  condition?: boolean;
  continueOnError: boolean;
  delay?: number;
  name: string;
  onComplete(response: any): void;
  onError(reason: ProgressError): void;
}

export interface ProgressStepSummary {
  completed: boolean;
  error: string | null;
  name: string;
  step: number;
}

export const useProgress = (
  steps: ProgressStep[],
  onComplete: () => void
): CurrentProgress => {
  const { makeRequest, loading, error, response } = useFetch(true);
  const { sortAsc } = useSort<ProgressStep>('step');
  steps = steps.filter((step) => step.condition ?? true).sort(sortAsc);
  const [currentStep, setCurrentStep] = useState(0);
  const [summary, setSummary] = useState<ProgressStepSummary[]>([]);

  useEffect(() => {
    if (currentStep <= steps.length - 1 && currentStep > 0) {
      processStep();
    }
    if (currentStep === steps.length - 1) {
      onComplete();
    }
  }, [currentStep]);

  useEffect(() => {
    if (!loading && response) {
      const step = steps[currentStep];
      if (step) {
        setSummary((summary) => [
          ...summary,
          { step: step.step, name: step.name, completed: true, error: null },
        ]);
        step.onComplete(response);
        setCurrentStep(currentStep + 1);
      }
    }
  }, [loading, response]);

  useEffect(() => {
    if (!loading && error) {
      const step = steps[currentStep];
      if (step) {
        step.onError({ code: error.statusText ?? null, reason: error.data });
        setSummary((summary) => [
          ...summary,
          {
            step: step.step,
            name: step.name,
            completed: step.continueOnError,
            error: error?.data,
          },
        ]);
        if (step.continueOnError) {
          setCurrentStep(currentStep + 1);
        } else {
          if (currentStep !== steps.length - 1)
            setSummary((summary) => [
              ...summary,
              ...steps.slice(currentStep + 1).map((s) => ({
                step: s.step,
                name: s.name,
                completed: false,
                error: `Unable to process because ${step.name} failed`,
              })),
            ]);
        }
      }
    }
  }, [loading, error]);

  interface ActionResponse extends ProgressStep {
    isSuccessful: boolean;
    error: string | null;
  }

  const processStandardAction = (
    step: ProgressStep
  ): Promise<ActionResponse> => {
    return new Promise<ActionResponse>(() => {
      if (step.action) step.action();
    });
  };

  const processStep = () => {
    const step = steps[currentStep];
    if (step) {
      if (step.axiosAction !== undefined) {
        step.axiosAction(makeRequest);
      } else if (step.action !== undefined) {
        setTimeout(() => {
          processStandardAction(step)
            .then(() => {
              step.onComplete(`Step ${step.step} completed successfully`);
            })
            .catch((reason) => {
              step.onError({ code: null, reason: reason });
              if (step.continueOnError) {
                setSummary((summary) => [
                  ...summary,
                  {
                    step: step.step,
                    name: step.name,
                    completed: step.continueOnError,
                    error: reason,
                  },
                ]);
              } else {
                setSummary((summary) => [
                  ...summary,
                  ...steps.slice(currentStep + 1).map((s) => ({
                    step: s.step,
                    name: s.name,
                    completed: false,
                    error: `Unable to process because ${step.name} failed`,
                  })),
                ]);
              }
            })
            .finally(() => {
              if (step.continueOnError) setCurrentStep(currentStep + 1);
            });
        }, step.delay ?? 1000);
      }
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setSummary([]);
  };

  const start = () => {
    processStep();
  };

  return {
    completed: steps.length === summary.length,
    currentStep: steps[currentStep] ?? {
      step: steps.length - 1,
      name: summary.some((step) => step.error)
        ? 'Complete (With Errors)'
        : 'Complete',
      continueOnError: false,
    },
    percentage: parseInt(((currentStep / steps.length) * 100).toFixed(0)),
    reset,
    start,
    summary,
    totalSteps: steps.length,
  };
};
